import {formatCents, orderByAscending, useAsync2} from "nate-react-api-helpers";
import {api} from "../../../../api/API";
import {Alert, Button, Checkbox} from "@mui/material";
import {Product} from "../../../../api/Products";
import {useEffect, useState} from "react";
import {css} from "@emotion/css";
import {grey} from "@mui/material/colors";
import {ImperialDistance} from "../../../../misc/ImperialDistance";
import {MetricDistance} from "../../../../misc/MetricDistance";

export type InvSelectorChange = {stocking: boolean; defaultPrice: string}
export function InventorySelector(props: {
    product?: number;
    autoDimensionEdited: boolean;
    onChange(value: Map<number, InvSelectorChange>): void;
    onSave(): void;
}) {
    const [changes, setChanges] = useState(new Map<number, InvSelectorChange>());
    const list = useAsync2(async (input) => {
        const product = input.product;
        if(!product) {
            return { type: "list", list: [] } as any
        }

        const list = await api.products.stockingOptions({
            product: product,
        })

        orderByAscending(list.list, item => {
            const parts = item.dimensions.split("x");
            if(parts[0].indexOf("mm") !== -1) {
                return MetricDistance.parse(parts[0]).universalValue()
            }

            return ImperialDistance.parse(parts[0]).universalValue()
        });

        return list;
    }, {product: props.product}, [props.product]);

    const onChange = props.onChange
    useEffect(() => {
        onChange(changes);
    }, [onChange, changes]);

    return (
        <div style={{marginLeft: -24, marginRight: -24}}>
            {(!props.product || props.autoDimensionEdited) && <>
                <Alert>
                    <div>
                        Please save your edits so we can generate the correct inventory options.
                    </div>
                    <Button onClick={props.onSave}>Save Changes & Reload</Button>
                </Alert>
            </>}

            {list.LoadingOrErrorElement}
            <table className={tableCss} style={{width: "100%"}}>
                <thead>
                <tr>
                    <th>Ref</th>
                    <th>Product Code</th>
                    <th>Dimensions</th>
                    <th>Default Price</th>
                    <th>Stocking</th>
                </tr>
                </thead>
                <tbody>
                {list.result?.list.map((v: Product) => {
                    const defaultPriceStr = v.defaultCostCents ? formatCents(v.defaultCostCents) : "";
                    const stocking = changes.has(v.id) ? changes.get(v.id)!.stocking : v.isStocking;
                    const priceStr = changes.has(v.id) ? changes.get(v.id)!.defaultPrice : defaultPriceStr;

                    const defaultObj = {
                        stocking: v.isStocking,
                        defaultPrice: defaultPriceStr,
                    }

                    return (
                        <tr key={v.id.toString()}>
                            <td style={{
                                fontWeight: changes.has(v.id) ? "bold" : undefined,
                            }}>{v.inventoryItem}</td>
                            <td style={{
                                fontWeight: changes.has(v.id) ? "bold" : undefined,
                            }}>{v.productCode}</td>
                            <td style={{textAlign: "right"}}>{v.dimensions}</td>
                            <td style={{padding: 0, textAlign: "right"}}>
                                <input type="text"
                                       style={{
                                           width: 80,
                                           height: 20,
                                           textAlign: "right",
                                           border: "1px solid " + grey["200"],
                                       }}
                                       value={priceStr}
                                       onChange={(e) => {
                                           const mp = new Map(changes);

                                           const obj = {
                                               stocking: stocking,
                                               defaultPrice: e.target.value,
                                           }

                                           if (isChange(defaultObj, obj)) {
                                               mp.set(v.id, obj);
                                           } else {
                                               mp.delete(v.id);
                                           }

                                           setChanges(mp);
                                       }}
                                />
                            </td>
                            <td style={{textAlign: "right", paddingTop: 0, paddingBottom: 0}}>
                                <Checkbox
                                    size="small"
                                    checked={stocking}
                                    onChange={(_, checked) => {
                                        const mp = new Map(changes);
                                        const obj = {
                                            stocking: checked,
                                            defaultPrice: priceStr,
                                        }

                                        if (isChange(defaultObj, obj)) {
                                            mp.set(v.id, obj);
                                        } else {
                                            mp.delete(v.id);
                                        }

                                        setChanges(mp);
                                    }}
                                />
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
}

function isChange(a: InvSelectorChange, b: InvSelectorChange) {
    return a.stocking !== b.stocking || a.defaultPrice !== b.defaultPrice;
}

const tableCss = css({
    borderCollapse: "collapse",

    "& td": {
        paddingLeft: 6,
        paddingRight: 6,
        fontSize: "0.8rem",
    },
    "& td:first-child": {
        paddingLeft: 24,
    }, "& td:last-child": {
        paddingRight: 16,
    },
    "& thead": {
        fontWeight: "bold",
        fontSize: "0.8rem",
    },
    "& tr:nth-child(even) td": {
        backgroundColor: grey["200"]
    }
})